import { logger, LogContext } from './logger';

// Re-export so consumers (e.g. errorSerializer) can import LogContext from the
// utils-facing module rather than reaching into ./logger directly.
export type { LogContext };

export interface ExtendedLogContext extends LogContext {
  [key: string]: any;
}

export class LoggerUtils {
  debug(message: string, context: ExtendedLogContext) {
    logger.debug(message, context);
  }

  info(message: string, context: ExtendedLogContext) {
    logger.info(message, context);
  }

  warning(message: string, context: ExtendedLogContext) {
    logger.warning(message, context);
  }

  error(message: string, error: Error | unknown, context: ExtendedLogContext) {
    logger.error(message, error, context);
  }

  performance = {
    start: (marker: string) => logger.startPerformanceMarker(marker),
    end: (marker: string) => logger.endPerformanceMarker(marker),
  };
}

export const loggerUtils = new LoggerUtils();

/**
 * Creates a log context with the calling file name included in the module field
 * @param functionName The name of the function generating the log
 * @param additionalContext Additional context to include in the log
 * @param fileName Optional file name override (defaults to extracting from stack trace)
 * @returns A log context object with module, function, and additional context
 */
export const createLogContext = (
  functionName: string,
  additionalContext: Record<string, any> = {},
  fileName?: string
): ExtendedLogContext => {
  // Extract the file name from the stack trace if not provided
  let module = fileName;

  if (!module) {
    try {
      const stackLines = new Error().stack?.split('\n') || [];
      // Skip the first two lines (Error and createLogContext call)
      const callerLine = stackLines[2] || '';
      // Extract file name from the stack trace
      const fileMatch =
        callerLine.match(/at\s+.*\s+\((.+?):\d+:\d+\)/) || callerLine.match(/at\s+(.+?):\d+:\d+/);

      if (fileMatch && fileMatch[1]) {
        // Get just the file name without the path
        const fullPath = fileMatch[1];
        const pathParts = fullPath.split(/[/\\]/);
        module = pathParts[pathParts.length - 1];
      } else {
        module = 'unknown';
      }
    } catch {
      // Fallback if stack trace parsing fails
      module = 'unknown';
    }
  }

  return {
    module,
    function: functionName,
    ...additionalContext,
  };
};
